/** @typedef {import('headlight-configurable-application').Pict} Pict */

const PictProvider = require('pict-provider');

const LOCAL_SCRATCH_ADDRESS = 'AppData.EntityBundleClientScratch';

/**
 * EntityBundleClient — executes a pict EntityProvider bundle (MeadowEntity
 * steps with `Bundle.<Entity>` destinations) against the entity_bundles
 * service in ONE round trip, falling back to local execution through this
 * app's own pict EntityProvider when the service is unreachable (offline
 * clients, environments without the service). Both paths return the same
 * `{ <Entity>: rows[] }` map, so consumers author the steps once.
 *
 * Remote: POST /1.0/EntityBundle/ExecutePictBundle. The response must carry
 * an array for every step's destination — a step the service rejected (e.g.
 * over its record cap) surfaces as a missing key and fails the remote path
 * rather than silently returning partial scope.
 *
 * Local: the same steps re-homed to a scratch address (PJU references
 * rewritten to match) and forced to `NoCount` pagination, so the reads stay
 * plain FilteredTo GETs an offline entity store can serve — pict's Query/
 * Count/Schema conveniences are avoided or degrade gracefully.
 *
 * Usage:
 *   const client = pict.providers.EntityBundleClient;
 *   const result = await client.executeBundle(steps);
 *   // result.Data.<Entity> => rows[], result.Source => 'remote'|'local'
 */
class EntityBundleClient extends PictProvider
{
	constructor(pFable, pOptions, pServiceHash)
	{
		super(pFable, pOptions, pServiceHash);
		/** @type {Pict} */
		this.pict;
	}

	/**
	 * Execute a bundle remotely, falling back to local execution.
	 *
	 * @param {Array<Record<string, any>>} pSteps - MeadowEntity steps with `Bundle.<Entity>` destinations.
	 * @param {{ Context?: Record<string, any>, AllowRemote?: boolean, AllowLocal?: boolean }} [pOptions]
	 * @return {Promise<{ Data: Record<string, Array<any>>, Source: ('remote'|'local') }>}
	 */
	async executeBundle(pSteps, pOptions)
	{
		const tmpOptions = pOptions || {};
		if (!Array.isArray(pSteps) || pSteps.length === 0)
		{
			return { Data: {}, Source: 'local' };
		}
		if (tmpOptions.AllowRemote !== false)
		{
			try
			{
				return { Data: await this._executeRemote(pSteps, tmpOptions.Context || {}), Source: 'remote' };
			}
			catch (pError)
			{
				if (tmpOptions.AllowLocal === false)
				{
					throw pError;
				}
				this.pict?.log?.warn(`[EntityBundleClient] Remote bundle execution failed (${pError?.message || pError}); executing locally.`);
			}
		}
		if (tmpOptions.AllowLocal === false)
		{
			throw new Error('EntityBundleClient: remote execution disabled and local execution disallowed.');
		}
		return { Data: await this._executeLocal(pSteps), Source: 'local' };
	}

	/**
	 * @param {Record<string, any>} pStep
	 * @return {string} the response key for a step — the last Destination segment.
	 */
	destinationKey(pStep)
	{
		const tmpDestination = String(pStep?.Destination || '');
		return tmpDestination.substring(tmpDestination.lastIndexOf('.') + 1);
	}

	/**
	 * @param {Array<Record<string, any>>} pSteps
	 * @param {Record<string, any>} pContext
	 * @return {Promise<Record<string, Array<any>>>}
	 */
	_executeRemote(pSteps, pContext)
	{
		return new Promise((fResolve, fReject) =>
		{
			this.pict.HeadlightRestClient.postJSON('EntityBundle/ExecutePictBundle',
				{ bundle: pSteps, context: pContext, skipEntities: [] },
				(pError, pBody) =>
				{
					if (pError)
					{
						return fReject(pError);
					}
					const tmpData = pBody && pBody.Data;
					if (!tmpData || typeof tmpData !== 'object')
					{
						return fReject(new Error('ExecutePictBundle response carried no Data.'));
					}
					/** @type {Record<string, Array<any>>} */
					const tmpResult = {};
					for (const tmpStep of pSteps)
					{
						const tmpKey = this.destinationKey(tmpStep);
						if (!Array.isArray(tmpData[tmpKey]))
						{
							return fReject(new Error(`ExecutePictBundle response is missing step result [${tmpKey}] (step rejected server-side?).`));
						}
						tmpResult[tmpKey] = tmpData[tmpKey];
					}
					fResolve(tmpResult);
				});
		});
	}

	/**
	 * Execute the steps through this app's pict EntityProvider. Destinations
	 * (and PJU references to them) are re-homed under a scratch address so a
	 * live app's own `Bundle.*` state is never touched, and `NoCount` keeps
	 * pagination offline-servable.
	 *
	 * @param {Array<Record<string, any>>} pSteps
	 * @return {Promise<Record<string, Array<any>>>}
	 */
	async _executeLocal(pSteps)
	{
		const tmpLocalSteps = pSteps.map((pStep) =>
		{
			const tmpStep = Object.assign({}, pStep, { NoCount: true });
			tmpStep.Destination = `${LOCAL_SCRATCH_ADDRESS}.${this.destinationKey(pStep)}`;
			if (typeof tmpStep.Filter === 'string')
			{
				tmpStep.Filter = tmpStep.Filter.split('^Bundle.').join(`^${LOCAL_SCRATCH_ADDRESS}.`);
			}
			return tmpStep;
		});
		try
		{
			await new Promise((fResolve, fReject) =>
			{
				this.pict.EntityProvider.gatherDataFromServer(tmpLocalSteps, (pError) => (pError ? fReject(pError) : fResolve(undefined)));
			});
			const tmpScratch = this.pict.AppData.EntityBundleClientScratch || {};
			/** @type {Record<string, Array<any>>} */
			const tmpResult = {};
			for (const tmpStep of pSteps)
			{
				const tmpKey = this.destinationKey(tmpStep);
				tmpResult[tmpKey] = Array.isArray(tmpScratch[tmpKey]) ? tmpScratch[tmpKey] : [];
			}
			return tmpResult;
		}
		finally
		{
			delete this.pict.AppData.EntityBundleClientScratch;
		}
	}
}

module.exports = EntityBundleClient;
